import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalanceRebuyRuntime";
const REBUY_SCRIPT_SELECTOR = 'script[src*="cdn.rebuyengine.com/onsite/js/rebuy.js"]';

interface RebuyRuntime {
  cleanup: () => void;
}

interface RebuyRuntimeWindow extends Window {
  [RUNTIME_KEY]?: RebuyRuntime;
}

function callIfFunction(candidate: unknown, context?: unknown): boolean {
  if (typeof candidate !== "function") {
    return false;
  }

  try {
    (candidate as () => void).call(context);
    return true;
  } catch {
    return false;
  }
}

function refreshRebuyRuntime(reason: string): boolean {
  const rebuy = window.Rebuy;
  const invoked = [
    callIfFunction(rebuy?.importSmartCartModule, rebuy),
    callIfFunction(rebuy?.init, rebuy),
    callIfFunction(rebuy?.widgets?.init, rebuy?.widgets),
    callIfFunction(rebuy?.Widgets?.init, rebuy?.Widgets),
    callIfFunction(rebuy?.Widget?.init, rebuy?.Widget),
    callIfFunction(rebuy?.SmartCart?.reloadCart, rebuy?.SmartCart),
    callIfFunction(rebuy?.SmartCart?.refresh, rebuy?.SmartCart),
    callIfFunction(rebuy?.cart?.refresh, rebuy?.cart)
  ].some(Boolean);

  document.documentElement.setAttribute("data-build-rebuy-adapter", "ready");
  document.documentElement.setAttribute(
    "data-build-rebuy-last-refresh",
    `${reason}:${invoked ? "invoked" : "no-op"}`
  );
  window.dispatchEvent(
    new CustomEvent("rebalance:rebuy:refresh", {
      detail: { reason, invoked }
    })
  );

  return invoked;
}

function dedupeRebuyScripts(): number {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>(REBUY_SCRIPT_SELECTOR));
  if (scripts.length <= 1) {
    return scripts.length;
  }

  scripts.slice(1).forEach((duplicateScript) => {
    duplicateScript.parentNode?.removeChild(duplicateScript);
  });
  return 1;
}

export function initRebuy(): void {
  const runtimeWindow = window as RebuyRuntimeWindow;
  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let refreshTimer = 0;
  let scriptLoadHandler: ((event: Event) => void) | null = null;
  let scriptEl: HTMLScriptElement | null = null;

  const scheduleRefresh = (reason: string, delayMs = 180): void => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshTimer = 0;
      dedupeRebuyScripts();
      refreshRebuyRuntime(reason);
    }, delayMs);
  };

  const handleGridUpdated = (): void => scheduleRefresh("collection-grid-updated");
  const handleAfterAdd = (): void => scheduleRefresh("cart-after-add");
  const unlistenRouteInitialized = themeEvents.on("route:initialized", ({ template }) => {
    scheduleRefresh(`route-initialized:${template}`);
  });

  document.addEventListener("baseline:collection:grid-updated", handleGridUpdated);
  document.body.addEventListener("baseline:modalcart:afteradditem", handleAfterAdd);

  scriptEl = document.querySelector<HTMLScriptElement>(REBUY_SCRIPT_SELECTOR);
  if (scriptEl) {
    scriptLoadHandler = () => scheduleRefresh("rebuy-script-load", 0);
    scriptEl.addEventListener("load", scriptLoadHandler);
  }

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      document.removeEventListener("baseline:collection:grid-updated", handleGridUpdated);
      document.body.removeEventListener("baseline:modalcart:afteradditem", handleAfterAdd);
      unlistenRouteInitialized();
      if (scriptEl && scriptLoadHandler) {
        scriptEl.removeEventListener("load", scriptLoadHandler);
      }
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = 0;
      }
    }
  };

  dedupeRebuyScripts();
  scheduleRefresh("adapter-init", 0);
}
